/**
 * VARIGHED — hvor længe noget tog eller mangler (Charlie 02-11-2026,
 * 03-ui-design §11.2, 12-conventions §100, [D-523]).
 *
 * Kort form til visning: ÉN enhed, et heltal og et mellemrum. Den næste enhed
 * tages først i brug, når der er mindst 3 af den:
 *
 *   under 10 sek.      "2,3 s"   (1 decimal)
 *   under 3 min        "45 s"    "150 s"
 *   under 3 timer      "43 m"    "135 m"
 *   under 3 dage       "14 t"    "50 t"
 *   under 3 måneder    "12 d"    "45 d"
 *   under 3 år         "3 mdr"   "15 mdr"
 *   derover            "4 år"
 *
 * Den fulde varighed, udspecificeret ("2 timer, 15 minutter og 12 sekunder"),
 * står ALTID i en tooltip ved siden af: i web via direktivet `v-duration`, der
 * kalder `formatDurationFullDa`. Inde i en tekst der selv er en tooltip, skrives
 * den fulde form direkte.
 *
 * Estimater får `~` foran hos kalderen ([D-107]); hjælperne giver kun tallet.
 * Måned = 30,4375 dage og år = 365,25 dage (samme som §11.1).
 */

const SEC = 1000;
const MIN = 60 * SEC;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
const YEAR = 365.25 * DAY;
const MONTH = YEAR / 12;

/** Enhederne i kort form; `limit` er det heltal hvor næste enhed overtager (3 af den). */
const SHORT_UNITS: ReadonlyArray<{ ms: number; label: string; limit: number }> = [
  { ms: SEC, label: 's', limit: 3 * MIN / SEC },
  { ms: MIN, label: 'm', limit: 3 * HOUR / MIN },
  { ms: HOUR, label: 't', limit: 3 * DAY / HOUR },
  { ms: DAY, label: 'd', limit: Math.round(3 * MONTH / DAY) },
  { ms: MONTH, label: 'mdr', limit: 3 * YEAR / MONTH },
  { ms: YEAR, label: 'år', limit: Infinity },
];

const dec1 = new Intl.NumberFormat('da-DK', { maximumFractionDigits: 1 });

/** Kort varighed: "45 s", "135 m", "3 mdr". Negative og ugyldige værdier giver "0 s". */
export function formatDurationDa(ms: number): string {
  const t = Number.isFinite(ms) ? Math.max(0, ms) : 0;
  const tenths = Math.round(t / 100) / 10;
  if (tenths < 10) return `${dec1.format(tenths)} s`;
  for (const u of SHORT_UNITS) {
    const v = Math.round(t / u.ms);
    if (v < u.limit) return `${v} ${u.label}`;
  }
  return `${Math.round(t / YEAR)} år`;
}

/** Enhederne i fuld form, største først: [ms, ental, flertal]. */
const FULL_UNITS: ReadonlyArray<[number, string, string]> = [
  [YEAR, 'år', 'år'],
  [MONTH, 'måned', 'måneder'],
  [DAY, 'dag', 'dage'],
  [HOUR, 'time', 'timer'],
  [MIN, 'minut', 'minutter'],
  [SEC, 'sekund', 'sekunder'],
];

/**
 * Fuld varighed, udspecificeret: "2 timer, 15 minutter og 12 sekunder".
 * Enheder på 0 udelades. Under 10 sek. med 1 decimal ("2,3 sekunder").
 */
export function formatDurationFullDa(ms: number): string {
  const t = Number.isFinite(ms) ? Math.max(0, ms) : 0;
  if (t < 10 * SEC) {
    const v = Math.round(t / 100) / 10;
    return `${dec1.format(v)} ${v === 1 ? 'sekund' : 'sekunder'}`;
  }
  let rest = Math.round(t / SEC) * SEC;
  const parts: string[] = [];
  for (const [unit, one, many] of FULL_UNITS) {
    const n = Math.floor(rest / unit);
    if (n <= 0) continue;
    rest -= n * unit;
    parts.push(`${n} ${n === 1 ? one : many}`);
  }
  if (parts.length === 1) return parts[0]!;
  return `${parts.slice(0, -1).join(', ')} og ${parts[parts.length - 1]}`;
}
